import * as React from 'react';
import type { UploadFile } from 'antd/lib/upload/interface';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import { selectApp } from '@/selectors/selectors';
import { uploadChecklistAttachments, getChecklistAttachmentURLs } from '@/api';
import { ChecklistAttachment, ChecklistAttachmentCommentTarget } from '@/types';

interface UseChecklistImageUploadInput {
    orgId: string;
    appId: string;
    commentTarget?: ChecklistAttachmentCommentTarget;
}

export function useChecklistImageUpload({ orgId, appId, commentTarget }: UseChecklistImageUploadInput) {
    const [images, setImages] = React.useState<UploadFile[]>([]);
    const [isUploadingImages, setIsUploadingImages] = React.useState(false);
    const [isLoadingAttachments, setIsLoadingAttachments] = React.useState(false);

    const auth = useSelector((state: RootState) => state.auth.authCredentials);
    const workflowId = useSelector((state: RootState) => selectApp(state, appId)?.workflowId);

    const uploadNewImages = async (): Promise<ChecklistAttachment[]> => {
        const newImages = images.filter((img) => img.originFileObj);
        const existingAttachments: ChecklistAttachment[] = images
            .filter((img) => !img.originFileObj)
            .map((img) => ({ s3Key: img.uid, fileName: img.name, contentType: img.type ?? '' }));

        let uploadedAttachments: ChecklistAttachment[] = [];
        if (newImages.length > 0 && workflowId && auth) {
            const files = newImages.map((img) => img.originFileObj as File).filter(Boolean);
            setIsUploadingImages(true);
            try {
                const response = await uploadChecklistAttachments(orgId, workflowId, files, auth, commentTarget);
                uploadedAttachments = response.data ?? [];
            } finally {
                setIsUploadingImages(false);
            }
        }

        return [...existingAttachments, ...uploadedAttachments];
    };

    const loadExistingAttachments = async (attachments?: ChecklistAttachment[]) => {
        if (!attachments?.length || !workflowId || !auth) {
            setImages([]);
            return;
        }

        const s3Keys = attachments.map((att) => att.s3Key).filter(Boolean);
        if (s3Keys.length === 0) {
            setImages([]);
            return;
        }

        setIsLoadingAttachments(true);
        try {
            const response = await getChecklistAttachmentURLs(orgId, workflowId, s3Keys, auth);
            const urls = response.data ?? {};
            setImages(
                attachments
                    .filter((att) => urls[att.s3Key])
                    .map(
                        (att) =>
                            ({
                                uid: att.s3Key,
                                name: att.fileName,
                                type: att.contentType,
                                url: urls[att.s3Key],
                            }) as UploadFile,
                    ),
            );
        } catch {
            setImages([]);
        } finally {
            setIsLoadingAttachments(false);
        }
    };

    const resetImages = () => setImages([]);

    return {
        images,
        setImages,
        isUploadingImages,
        isLoadingAttachments,
        uploadNewImages,
        loadExistingAttachments,
        resetImages,
    };
}
